import { useFlagsV2 } from "@/utils/hooks/use-flags-grpc";
import { useDANGEROUSisChalkEmployee } from "@/utils/hooks/use-is-chalk-employee";

export interface ExplorerUiDisabledState {
  /**
   * True when the `disable_explorer_ui` flag is on. Treated as `true` while flags are
   * still loading so the online/offline/SQL explorer surfaces never flash into view
   * before we confirm they should be shown.
   */
  explorerUiDisabled: boolean;
  /**
   * True when the explorer UI should be hidden from the current viewer. Chalk employees
   * (in admin mode) keep access — they retain the navbar links under the admin visual
   * treatment and can still open the pages/inline surfaces — so this is `false` for them
   * even when the flag is on. Uses the loading fallback, so this is `true` while flags load
   * (hide-by-default); consumers that instead want a loading placeholder should branch on
   * `explorerUiLoading` first.
   */
  explorerUiHiddenForViewer: boolean;
  /**
   * True while flags are still loading. Consumers that render a skeleton placeholder during
   * load (rather than hiding by default) branch on this before `explorerUiHiddenForViewer`:
   * once flags resolve this is `false` and `explorerUiHiddenForViewer` reflects the exact
   * flag value.
   */
  explorerUiLoading: boolean;
}

/**
 * Central gate for the `disable_explorer_ui` flag, which hides the online/offline/SQL
 * query explorers (navbar links, pages, and inline SQL surfaces). The flag defaults to
 * hiding while flags load, and chalk employees keep access.
 */
export const useExplorerUiDisabled = (): ExplorerUiDisabledState => {
  const flags = useFlagsV2();
  const isChalkEmployee = useDANGEROUSisChalkEmployee();
  const explorerUiDisabled = flags.enabledWithLoadingFallback("disable_explorer_ui", true);
  return {
    explorerUiDisabled,
    explorerUiHiddenForViewer: explorerUiDisabled && !isChalkEmployee,
    explorerUiLoading: !flags.ready,
  };
};
